import type { ScenarioAnalysis } from '@prm/shared';
import { pct } from '../format';
import { PanelHead, StatusPill } from './Panel';

const KIND_LABEL: Record<string, string> = {
  cluster: 'CLUSTER',
  concentration: 'STACKED',
  'single-name': 'NAME',
  sector: 'SECTOR',
  'hedge-dependence': 'HEDGE',
  'vol-overage': 'VOL',
  stress: 'STRESS',
  data: 'DATA',
};

/**
 * "Where you are vulnerable" — each row is a named exposure with a sentence,
 * not a ranked list of tickers.
 *
 * A contributor table answers which position carries the most variance. It does
 * not answer what would hurt you, because that is usually a shape — a cluster, a
 * concentration, a dependence — that no single row reveals. The raw table is
 * still there, one click away under Detail.
 */
export function VulnerabilityPanel({ scenario }: { scenario: ScenarioAnalysis }) {
  const { vulnerabilities } = scenario;
  const danger = vulnerabilities.filter((v) => v.severity === 'danger').length;

  // One shared denominator so bar lengths compare across rows: every row is
  // measured as a share of the book, whichever field carries it.
  const shares = vulnerabilities.map((v) => v.riskShare ?? v.weight ?? 0);
  const scale = Math.max(...shares, 0.25);

  return (
    <section className="panel">
      <PanelHead
        title="Where you are vulnerable"
        description={
          vulnerabilities.length === 0
            ? 'No named exposure exceeds its threshold.'
            : 'Named exposures, worst first — the shape of the risk, not the largest ticker.'
        }
        sub={
          <StatusPill tone={danger > 0 ? 'bad' : vulnerabilities.length > 0 ? 'warn' : 'good'}>
            {vulnerabilities.length === 0 ? 'clear' : `${danger} of ${vulnerabilities.length} serious`}
          </StatusPill>
        }
      />

      {vulnerabilities.length === 0 ? (
        <div className="placeholder-note">
          No named vulnerability. Every policy limit is satisfied and no cluster, concentration or dependence exceeds
          its threshold. The raw contributor table under Detail still shows where the variance sits.
        </div>
      ) : (
        <div className="vulnerability-list">
          {vulnerabilities.map((v) => {
            const share = v.riskShare ?? v.weight ?? null;
            return (
              <article className="vulnerability-row" key={v.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="row-title">
                    <span className={`minichip minichip--${v.severity === 'danger' ? 'breach' : 'warn'}`}>
                      {KIND_LABEL[v.kind] ?? v.kind.toUpperCase()}
                    </span>
                    <span>{v.title}</span>
                  </div>
                  <p className="row-detail">{v.sentence}</p>
                </div>

                <div className="risk-bar">
                  {share === null ? null : (
                    <div
                      className={`risk-bar__fill ${v.severity === 'danger' ? 'is-bad' : 'is-warn'}`}
                      style={{ width: `${Math.min(100, (Math.abs(share) / scale) * 100)}%` }}
                    />
                  )}
                </div>

                <div
                  className={`risk-value ${v.severity === 'danger' ? 'text-bad' : 'text-warn'}`}
                  title={v.riskShare !== null ? 'Share of portfolio risk' : 'Share of book'}
                >
                  {share === null ? '—' : pct(share)}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="footnote">
        Share is marginal contribution to risk where the decomposition covers the exposure, and share of gross where
        it does not; the tooltip on each value says which. Bars are scaled to the largest row, not to 100%.
      </div>
    </section>
  );
}
